// Full-screen seat editor for an existing reservation. Reuses the public
// SeatMap. The reservation's own seats start selected and are excluded from the
// "taken" set so they can be dropped; seats held by OTHER reservations stay
// blocked. Save replaces the whole set via PATCH /admin/reservations/:id/seats.

import { SeatMap } from '@/features/booking/SeatMap/index.tsx';
import { useSeatmap } from '@/features/booking/api/useSeatmap.ts';
import { useSeatmapState } from '@/features/booking/api/useSeatmapState.ts';
import { cn } from '@/lib/cn.ts';
import { MVP_EVENT_ID, type SeatDto } from '@okn/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { Armchair, Check, Minus, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { adminApi } from './auth.ts';
import { Button } from './ui/Button.tsx';
import { Spinner } from './ui/Spinner.tsx';
import { useToast } from './ui/Toast.tsx';

type EditableReservation = {
  id: string;
  referenceCode: string;
  fullName: string;
  status: string;
  seats: Array<{ id: string; label: string; level: string }>;
};

type Props = {
  open: boolean;
  reservation: EditableReservation;
  onClose: () => void;
  onSaved: () => void;
};

const STATE_TOKEN = 'admin-seat-editor-readonly';

export function SeatEditor({ open, reservation, onClose, onSaved }: Props) {
  const toast = useToast();
  const map = useSeatmap(MVP_EVENT_ID);
  const { state } = useSeatmapState(MVP_EVENT_ID, STATE_TOKEN);

  const ownSeatIds = useMemo(
    () => new Set(reservation.seats.map((s) => s.id)),
    [reservation.seats],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(ownSeatIds));
  const [busy, setBusy] = useState(false);

  // Reset the working selection whenever the editor (re)opens for a reservation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on open / id change
  useEffect(() => {
    if (open) setSelected(new Set(ownSeatIds));
  }, [open, reservation.id]);

  // Seats taken by OTHER reservations (own seats removed so they're editable).
  const blocked = useMemo(() => {
    const reserved = new Set<string>();
    const sold = new Set<string>();
    const held = new Set<string>();
    for (const sid of state?.reservedSeatIds ?? []) if (!ownSeatIds.has(sid)) reserved.add(sid);
    for (const sid of state?.soldSeatIds ?? []) if (!ownSeatIds.has(sid)) sold.add(sid);
    for (const sid of state?.heldSeatIds ?? []) if (!ownSeatIds.has(sid)) held.add(sid);
    return { reserved, sold, held };
  }, [state, ownSeatIds]);

  const stateCtx = useMemo(
    () => ({
      reservedSeatIds: blocked.reserved,
      soldSeatIds: blocked.sold,
      heldSeatIds: blocked.held,
    }),
    [blocked],
  );

  const selectedSeats = useMemo<SeatDto[]>(() => {
    if (map.status !== 'ready') return [];
    return map.data.allSeats.filter((s) => selected.has(s.id));
  }, [map, selected]);

  const added = selectedSeats.filter((s) => !ownSeatIds.has(s.id)).length;
  const removed =
    reservation.seats.length - selectedSeats.filter((s) => ownSeatIds.has(s.id)).length;
  const changed = added > 0 || removed > 0;

  const save = async () => {
    setBusy(true);
    try {
      await adminApi(`/admin/reservations/${reservation.id}/seats`, {
        method: 'PATCH',
        body: { seatIds: Array.from(selected) },
      });
      toast.success(`Koltuklar güncellendi · ${selected.size} koltuk`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[900] flex flex-col bg-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {/* Header */}
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-4 py-3 md:px-6">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-quiet text-accent">
                <Armchair className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-sm font-medium tracking-tight">Koltukları Düzenle</h2>
                <p className="mono text-xs text-text-faint tnum">
                  {reservation.referenceCode} · {reservation.fullName}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              className="rounded-md p-2 text-text-faint transition-colors hover:bg-surface hover:text-text"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          {/* Body */}
          <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_340px]">
            <div className="min-h-0 border-border border-b p-3 lg:border-r lg:border-b-0">
              {map.status === 'ready' ? (
                <SeatMap
                  data={map.data}
                  selectedIds={selected}
                  stateContext={stateCtx}
                  onSelect={(seat) => {
                    if (!seat.isSellable) return;
                    if (blocked.sold.has(seat.id) || blocked.reserved.has(seat.id)) return;
                    setSelected((prev) => {
                      const n = new Set(prev);
                      if (n.has(seat.id)) n.delete(seat.id);
                      else n.add(seat.id);
                      return n;
                    });
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-text-faint">
                  <Spinner /> Koltuk haritası yükleniyor…
                </div>
              )}
            </div>

            {/* Summary */}
            <aside className="flex min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex items-end gap-3">
                  <div>
                    <p className="kicker">Yeni seçim</p>
                    <p className="mt-1 font-display text-5xl leading-none tnum">{selected.size}</p>
                  </div>
                  <p className="pb-1.5 text-xs text-text-faint">
                    önceki {reservation.seats.length} koltuk
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs',
                      added > 0
                        ? 'bg-[rgba(106,168,79,0.15)] text-success'
                        : 'bg-surface text-text-faint',
                    )}
                  >
                    <Plus className="h-3 w-3" /> {added} eklendi
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs',
                      removed > 0 ? 'bg-accent-faint text-accent' : 'bg-surface text-text-faint',
                    )}
                  >
                    <Minus className="h-3 w-3" /> {removed} çıkarıldı
                  </span>
                </div>

                {selectedSeats.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {selectedSeats.map((s) => (
                      <span
                        key={s.id}
                        className={cn(
                          'mono rounded-md border px-2 py-0.5 text-xs tnum',
                          ownSeatIds.has(s.id)
                            ? 'border-border bg-surface text-text'
                            : 'border-success/40 bg-[rgba(106,168,79,0.12)] text-success',
                        )}
                      >
                        {s.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-text-faint">
                    En az bir koltuk seçili olmalı. Haritadan koltuk ekleyin.
                  </p>
                )}

                <p className="mt-5 text-[11px] leading-relaxed text-text-faint">
                  Haritadan koltuğa tıklayarak ekleyip çıkarabilirsiniz. Yeşil olanlar yeni
                  eklenenler.
                  {reservation.status === 'paid' &&
                    ' Bu rezervasyon onaylanmış — değişiklik anında geçerli olur.'}
                </p>
              </div>

              <div className="flex shrink-0 gap-2 border-t border-border p-4">
                <Button variant="ghost" className="flex-1" onClick={onClose} disabled={busy}>
                  Vazgeç
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  icon={busy ? undefined : <Check className="h-4 w-4" />}
                  loading={busy}
                  disabled={selected.size === 0 || !changed}
                  onClick={save}
                >
                  Kaydet
                </Button>
              </div>
            </aside>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
